// src/utils/googleApi.js
// Centralized Google API helpers for Sheets, Drive, and Calendar

const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

/**
 * Fetch menu data from a published Google Sheet.
 *
 * Expected Sheet columns (Row 1 = headers):
 *   A: Category    B: Name    C: Price    D: Description    E: ImageName
 *
 * Returns: Array of { category, items: [{ name, price, description, imageName }] }
 * Returns null if not configured or on error.
 */
export async function fetchMenuFromSheet(sheetId) {
  if (!sheetId) {
    console.warn('[LFBBQ] No Google Sheet ID configured.');
    return null;
  }

  const range = 'Sheet1!A:E';
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}?key=${API_KEY}`;

  try {
    console.log('[LFBBQ] Fetching menu from Sheets...');
    const response = await fetch(url);
    const data = await response.json();

    if (data.error) {
      console.error('[LFBBQ] Sheets API error:', data.error.code, data.error.message);
      return null;
    }

    if (!data.values || data.values.length < 2) {
      console.warn('[LFBBQ] Sheet is empty or has no data rows.');
      return null;
    }

    console.log('[LFBBQ] Sheet data loaded:', data.values.length - 1, 'menu items found');

    const rows = data.values.slice(1);
    const categoryMap = {};
    const categoryOrder = [];

    rows.forEach(row => {
      const category = (row[0] || '').trim();
      const name = (row[1] || '').trim();
      const price = parseFloat(row[2]) || 0;
      const description = (row[3] || '').trim();
      const imageName = (row[4] || '').trim();

      if (!category || !name) return;

      if (!categoryMap[category]) {
        categoryMap[category] = [];
        categoryOrder.push(category);
      }

      categoryMap[category].push({ name, price, description, imageName });
    });

    return categoryOrder.map(category => ({
      category,
      items: categoryMap[category],
    }));
  } catch (error) {
    console.error('[LFBBQ] Error fetching menu from Google Sheets:', error);
    return null;
  }
}

/**
 * Fetch image files from a public Google Drive folder.
 * Returns: Array of { id, name, src, srcLarge } or null on error.
 */
export async function fetchImagesFromDrive(folderId) {
  if (!folderId) {
    return null;
  }

  const query = `'${folderId}' in parents and mimeType contains 'image/' and trashed = false`;
  const url = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&key=${API_KEY}&fields=files(id,name,mimeType)&pageSize=100`;

  try {
    console.log('[LFBBQ] Fetching images from Drive folder:', folderId);
    const response = await fetch(url);
    const data = await response.json();

    if (data.error) {
      console.error('[LFBBQ] Drive API error:', data.error.code, data.error.message);
      return null;
    }

    if (!data.files || data.files.length === 0) {
      console.warn('[LFBBQ] No images found in Drive folder:', folderId);
      return null;
    }

    console.log('[LFBBQ] Drive images loaded:', data.files.map(f => f.name));

    return data.files.map(file => ({
      id: file.id,
      name: file.name,
      src: `https://lh3.googleusercontent.com/d/${file.id}`,
      srcLarge: `https://lh3.googleusercontent.com/d/${file.id}`,
    }));
  } catch (error) {
    console.error('[LFBBQ] Error fetching images from Google Drive:', error);
    return null;
  }
}

/**
 * Fetch TikTok video data from a Google Sheet.
 *
 * Expected Sheet columns (Row 1 = headers):
 *   A: URL (full TikTok video URL)
 *   B: Caption (optional display caption)
 *
 * Returns: Array of { url, caption, videoId } or null.
 */
export async function fetchTikToksFromSheet(sheetId) {
  if (!sheetId) {
    return null;
  }

  const range = 'Sheet1!A:B';
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}?key=${API_KEY}`;

  try {
    console.log('[LFBBQ] Fetching TikToks from Sheets...');
    const response = await fetch(url);
    const data = await response.json();

    if (data.error) {
      console.error('[LFBBQ] TikTok Sheet API error:', data.error.code, data.error.message);
      return null;
    }

    if (!data.values || data.values.length < 2) {
      console.warn('[LFBBQ] TikTok sheet is empty or has no data rows.');
      return null;
    }

    console.log('[LFBBQ] TikTok data loaded:', data.values.length - 1, 'videos found');

    const rows = data.values.slice(1);

    return rows
      .map(row => {
        const tiktokUrl = (row[0] || '').trim();
        const caption = (row[1] || '').trim();

        if (!tiktokUrl) return null;

        // Extract video ID from various TikTok URL formats:
        //   https://www.tiktok.com/@user/video/1234567890
        //   https://www.tiktok.com/@user/video/1234567890?...
        //   https://vm.tiktok.com/ZMxxxxxx/
        //   Plain video ID: 1234567890
        let videoId = null;
        const videoMatch = tiktokUrl.match(/video\/(\d+)/);
        if (videoMatch) {
          videoId = videoMatch[1];
        } else if (/^\d+$/.test(tiktokUrl)) {
          videoId = tiktokUrl; // raw ID
        }

        return { url: tiktokUrl, caption, videoId };
      })
      .filter(Boolean);
  } catch (error) {
    console.error('[LFBBQ] Error fetching TikToks from Sheets:', error);
    return null;
  }
}

/**
 * Fetch catering menu options from a Google Sheet.
 *
 * Expected Sheet columns (Row 1 = headers):
 *   A: Type ("Protein" or "Side")
 *   B: Name (e.g. "Smoked Brisket")
 *
 * If REACT_APP_GOOGLE_SHEET_CATERING_ID is set, uses that sheet.
 * Otherwise falls back to extracting categories from the main menu sheet.
 *
 * Returns: { proteins: string[], sides: string[] } or null.
 */
export async function fetchCateringOptions(cateringSheetId, menuSheetId) {
  // Try dedicated catering sheet first
  const sheetId = cateringSheetId || menuSheetId;
  if (!sheetId) return null;

  if (cateringSheetId) {
    const range = 'Sheet1!A:B';
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${cateringSheetId}/values/${range}?key=${API_KEY}`;
    try {
      const response = await fetch(url);
      const data = await response.json();
      if (data.error || !data.values || data.values.length < 2) return null;

      const rows = data.values.slice(1);
      const proteins = [];
      const sides = [];

      rows.forEach(row => {
        const type = (row[0] || '').trim().toLowerCase();
        const name = (row[1] || '').trim();
        if (!name) return;
        if (type === 'protein' || type === 'meat') proteins.push(name);
        else if (type === 'side') sides.push(name);
      });

      return { proteins, sides };
    } catch {
      return null;
    }
  }

  // Fallback: derive from the main menu sheet categories
  try {
    const menuData = await fetchMenuFromSheet(menuSheetId);
    if (!menuData) return null;

    const proteins = [];
    const sides = [];

    menuData.forEach(section => {
      const cat = section.category.toLowerCase();
      if (cat.includes('protein') || cat.includes('meat') || cat.includes('bbq') || cat.includes('smoked')) {
        section.items.forEach(item => proteins.push(item.name));
      } else if (cat.includes('side')) {
        section.items.forEach(item => sides.push(item.name));
      }
    });

    return proteins.length || sides.length ? { proteins, sides } : null;
  } catch {
    return null;
  }
}

/**
 * Fetch a single hero image from a Google Drive folder.
 * Expects the folder to contain one image — uses the first one found.
 * Returns: image URL string or null.
 */
export async function fetchHeroImage(folderId) {
  if (!folderId) {
    return null;
  }

  const images = await fetchImagesFromDrive(folderId);
  if (!images || images.length === 0) return null;

  return images[0].srcLarge;
}

/**
 * Fetch upcoming events from Google Calendar.
 * Returns: Array of calendar event objects (empty array if not configured).
 */
export async function fetchCalendarEvents(calendarId) {
  if (!calendarId) {
    console.warn('[LFBBQ] No Google Calendar ID configured.');
    return [];
  }

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  const maxResults = 20;
  const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(
    calendarId
  )}/events?key=${API_KEY}&timeMin=${todayStart}&maxResults=${maxResults}&singleEvents=true&orderBy=startTime`;

  try {
    console.log('[LFBBQ] Fetching calendar events...');
    const response = await fetch(url);
    const data = await response.json();

    if (data.error) {
      console.error('[LFBBQ] Calendar API error:', data.error.code, data.error.message);
      return [];
    }

    if (!data.items) {
      console.warn('[LFBBQ] No events returned.');
      return [];
    }

    console.log('[LFBBQ] Raw calendar events:', data.items.map(e => ({
      summary: e.summary,
      location: e.location,
      start: e.start,
    })));

    const todayStr = todayStart.slice(0, 10);

    const currentDayEvents = data.items.filter(event => {
      if (event.start.dateTime) return event.start.dateTime.startsWith(todayStr);
      if (event.start.date) return event.start.date === todayStr;
      return false;
    });

    const futureEvents = data.items
      .filter(event => {
        const eventDate = event.start.dateTime
          ? new Date(event.start.dateTime)
          : new Date(event.start.date);
        return eventDate > now;
      })
      .filter(event => {
        if (event.start.dateTime) return !event.start.dateTime.startsWith(todayStr);
        if (event.start.date) return event.start.date !== todayStr;
        return false;
      })
      .slice(0, 6);

    const combined = [...currentDayEvents, ...futureEvents];
    combined.sort((a, b) => {
      const aStart = a.start.dateTime ? new Date(a.start.dateTime) : new Date(a.start.date);
      const bStart = b.start.dateTime ? new Date(b.start.dateTime) : new Date(b.start.date);
      return aStart - bStart;
    });

    console.log('[LFBBQ] Processed', combined.length, 'events');
    return combined;
  } catch (error) {
    console.error('[LFBBQ] Error fetching calendar events:', error);
    return [];
  }
}

/**
 * Build a Google Drive image URL from a filename and a list of Drive images.
 * Returns null if no match found (no local fallback).
 */
export function getDriveImageUrl(imageName, driveImages) {
  if (!imageName) return null;
  if (!driveImages || driveImages.length === 0) return null;

  const match = driveImages.find(
    img => img.name.toLowerCase() === imageName.toLowerCase()
  );

  if (!match) {
    console.warn(`[LFBBQ] No Drive image match for "${imageName}". Available:`, driveImages.map(i => i.name));
  }

  return match ? match.src : null;
}
